import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { FeatherLoader, useErrorService } from '@abroad/components'
import { ExperienceSelect } from '../form'
import CONSTANTS from '../../utils/constants'
import API from '../../utils/API'
import ModuleMediaAccordion from './ModuleMediaAccordion'
import SearchMedias from './SearchMedias'
import { JourneyMediasContext } from '../../utils/contexts'
import useDebounce from '../common/useDebounce'

const JourneysLibrary = ({
  title,
  journeyType,
  hideTitle = false,
  experienceChrome = false,
}) => {
  const [hasSearchByInput, setHasSearchByInput] = useState(false)
  const [libSearch, setLibSearch] = useState('')
  const debouncedLibSearch = useDebounce(libSearch, 350)
  const [currentCategorySelected, setCurrentCategorySelected] =
    useState('breakthrough')
  const [moduleMediaDetails, setModuleMediaDetails] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const { setMediasList } = useContext(JourneyMediasContext)
  const { showError } = useErrorService()

  const options = useMemo(() => {
    const raw = CONSTANTS.JOURNEYS_CATEGORY || []
    return raw.map((opt) => ({
      value: opt.value || opt.id || opt.label,
      label: opt.label || opt.name || opt.value,
    }))
  }, [])

  const getApiURL = useCallback(() => {
    switch (currentCategorySelected) {
      case 'breakthrough':
        return API.ClientAPI.programs.getPrograms('5fddc237a483be3860dea6f2')
      case 'evolution':
        return API.ClientAPI.programs.getPrograms('60e885f48ea7c31048bc553d')
      case 'resilience':
        return API.ClientAPI.programs.getPrograms('639970530a71c612b0026943')
      case 'essentials':
        return API.ClientAPI.programs.getPrograms('656dc22e079eab484d6e3288')
      case 'self awareness':
        return API.ClientAPI.mediaLibrary.getMediaByCategory(
          '5b46b176ce416b175ff98e17',
        )
      case 'team cohesion':
        return API.ClientAPI.mediaLibrary.getMediaByCategory(
          '5b46b176ce416b175ff98e2f',
        )
      case 'vision & impact':
        return API.ClientAPI.mediaLibrary.getMediaByCategory(
          '5b46b176ce416b175ff98de6',
        )
      case 'manager efficacy':
        return API.ClientAPI.mediaLibrary.getMediaByCategory(
          '639c067da4d9940fd87f216d',
        )
      default:
        return null
    }
  }, [currentCategorySelected])

  useEffect(() => {
    const load = async () => {
      const path = getApiURL()
      if (!path) return
      setIsLoading(true)
      try {
        const { data } = await path
        setModuleMediaDetails(data)
      } catch (e) {
        showError(e)
      }
      setIsLoading(false)
    }
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCategorySelected])

  const addMediaHandler = (media) => {
    setMediasList((prev) => [...prev, media])
  }

  const isModuleCategory = [
    'breakthrough',
    'evolution',
    'resilience',
    'essentials',
  ].includes(currentCategorySelected)

  const searchQuery = experienceChrome
    ? String(debouncedLibSearch || '').trim()
    : ''
  const isSearching = experienceChrome
    ? searchQuery.length > 0
    : hasSearchByInput

  const handleCategoryChange = (e) => {
    setCurrentCategorySelected(e.target.value)
    if (experienceChrome) {
      setLibSearch('')
    }
  }

  return (
    <div>
      {!hideTitle && title ? (
        <h3 className='font-montserrat font-18 font-bold leading-relaxed letter-spacing-minus-04 text-center'>
          {title}
        </h3>
      ) : null}

      <div className='experience-lib-compose'>
        {experienceChrome ? (
          <div className='experience-lib-search-wrap'>
            <input
              type='text'
              className='experience-lib-search'
              placeholder='Search audio title…'
              value={libSearch}
              onChange={(e) => setLibSearch(e.target.value)}
              autoComplete='off'
            />
          </div>
        ) : (
          <SearchMedias
            key={currentCategorySelected}
            render={(isSearchByInput) => setHasSearchByInput(isSearchByInput)}
            journeyType={journeyType}
            addMediaHandler={addMediaHandler}
            placeholder='Search audio title…'
            inputOnly
          />
        )}
        {experienceChrome || !hasSearchByInput ? (
          <div className='experience-lib-program-wrap'>
            <ExperienceSelect
              value={currentCategorySelected}
              onChange={handleCategoryChange}
              options={options}
              aria-label='Library program'
              className='experience-lib-program'
            />
          </div>
        ) : null}

        {isSearching && !experienceChrome ? null : isLoading ? (
          <div className='experience-lib-modules d-flex justify-content-center align-items-center my-3'>
            <FeatherLoader />
          </div>
        ) : (
          <div className='experience-lib-modules'>
            <ModuleMediaAccordion
              moduleMediaDetails={moduleMediaDetails}
              journeyType={journeyType}
              dropDownSelected={isModuleCategory ? 'module' : 'media'}
              isBreakthroughOrEvoModule={isModuleCategory}
              currentCategorySelected={currentCategorySelected}
              experienceChrome={experienceChrome}
              searchQuery={searchQuery}
            />
          </div>
        )}
      </div>
    </div>
  )
}

export default JourneysLibrary
